import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Loader2, Building2, DollarSign, TrendingUp, Percent, CheckCircle,
  Clock, AlertCircle, Wallet, ArrowUpRight, BarChart3, Banknote, Shield
} from "lucide-react";
import { Card, GoldButton } from "../../components/shared/UIComponents";
import { fmtUSD } from "../../utils/currency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  raising: "bg-blue-100 text-blue-700",
  deploying: "bg-emerald-100 text-emerald-700",
  harvesting: "bg-amber-100 text-amber-700",
  closed: "bg-gray-100 text-gray-600",
};

const SUB_COLORS = {
  pending_approval: "bg-amber-100 text-amber-700",
  active: "bg-emerald-100 text-emerald-700",
  called: "bg-blue-100 text-blue-700",
  rejected: "bg-red-100 text-red-600",
};

export default function InvestorFundsTab() {
  const [funds, setFunds] = useState([]);
  const [myFunds, setMyFunds] = useState([]);
  const [fundPortalOpps, setFundPortalOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribeModal, setSubscribeModal] = useState(null);
  const [subAmount, setSubAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [fundsRes, myRes, portalRes] = await Promise.all([
        axios.get(`${API}/funds/`, { headers }),
        axios.get(`${API}/funds/investor/my-funds`, { headers }),
        axios.get(`${API}/fund-portal/opportunities`, { headers }).catch(() => ({ data: { opportunities: [] } })),
      ]);
      setFunds(fundsRes.data.funds || []);
      setMyFunds(myRes.data.subscriptions || []);
      setFundPortalOpps(portalRes.data.opportunities || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubscribe = async () => {
    if (!subscribeModal || !subAmount) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/funds/subscribe`, {
        fund_id: subscribeModal.fund_id,
        subscription_amount: parseFloat(subAmount),
      }, { headers });
      setSubscribeModal(null);
      setSubAmount("");
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to subscribe");
    }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;

  const subscribedFundIds = new Set(myFunds.map(s => s.fund_id));

  return (
    <div className="space-y-6" data-testid="investor-funds-tab">
      {/* My Subscriptions */}
      {myFunds.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-bold text-[#333] uppercase tracking-wider">My Fund Subscriptions</h3>
          {myFunds.map((sub) => {
            const fund = sub.fund || {};
            return (
              <motion.div key={sub.commitment_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl border border-cream-warm bg-white p-5 hover:shadow-md transition-shadow"
                data-testid={`my-fund-${sub.commitment_id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-base font-bold text-[#0A0A0A] flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-gold-dark" /> {fund.name || sub.fund_name}
                    </h4>
                    <div className="flex gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${STATUS_COLORS[fund.status] || "bg-gray-100 text-gray-600"}`}>{fund.status?.toUpperCase()}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${SUB_COLORS[sub.status] || ""}`}>{sub.status?.replace("_", " ").toUpperCase()}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-[#999]">My Subscription</div>
                    <div className="text-xl font-black text-emerald-600">${(sub.subscription_amount || 0).toLocaleString()}</div>
                  </div>
                </div>

                {/* Capital Call Progress */}
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-[#888] mb-1">
                    <span>Called: ${(sub.called_amount || 0).toLocaleString()}</span>
                    <span>Unfunded: ${(sub.unfunded || 0).toLocaleString()}</span>
                  </div>
                  <div className="w-full bg-cream rounded-full h-2">
                    <div className="h-2 rounded-full bg-blue-500 transition-all" style={{ width: `${sub.call_pct || 0}%` }} />
                  </div>
                  <div className="text-right text-[10px] text-blue-600 mt-0.5">{sub.call_pct}% called</div>
                </div>

                {/* Fund Metrics */}
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {[
                    { label: "Fund NAV", value: `$${((fund.nav || 0) / 1e3).toFixed(0)}K`, icon: BarChart3 },
                    { label: "MOIC", value: `${fund.moic || 0}x`, icon: TrendingUp },
                    { label: "Pref Return", value: `${fund.preferred_return_pct || 8}%`, icon: Percent },
                    { label: "Carry", value: `${fund.carried_interest_pct || 20}%`, icon: ArrowUpRight },
                    { label: "Deployed", value: `$${((fund.total_deployed || 0) / 1e3).toFixed(0)}K`, icon: DollarSign },
                    { label: "Deals", value: fund.deal_count || 0, icon: Banknote },
                  ].map((m) => {
                    const Icon = m.icon;
                    return (
                      <div key={m.label} className="rounded-xl bg-cream border border-cream-warm p-2.5 text-center">
                        <Icon className="h-3 w-3 text-gold-dark mx-auto mb-0.5" />
                        <div className="text-[9px] text-[#999] uppercase">{m.label}</div>
                        <div className="text-sm font-bold text-[#333]">{m.value}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Distributions */}
                {(sub.distributed_amount || 0) > 0 && (
                  <div className="mt-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                      <span className="text-emerald-700 font-medium">Distributions received: ${(sub.distributed_amount || 0).toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Available Funds */}
      {funds.filter(f => ["raising", "open"].includes(f.status) && !subscribedFundIds.has(f.fund_id)).length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-bold text-[#333] uppercase tracking-wider">Open Funds</h3>
          {funds.filter(f => ["raising", "open"].includes(f.status) && !subscribedFundIds.has(f.fund_id)).map((fund) => (
            <Card key={fund.fund_id} hover={false}>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-bold text-[#0A0A0A] flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-gold-dark" /> {fund.name}
                  </h4>
                  <p className="text-xs text-[#888] mt-1">
                    Target: ${(fund.target_size || 0).toLocaleString()} |
                    {fund.subscription_pct}% subscribed |
                    {fund.preferred_return_pct}% pref return |
                    Min: ${(fund.min_subscription || 0).toLocaleString()}
                  </p>
                </div>
                <GoldButton onClick={() => { setSubscribeModal(fund); setSubAmount(""); }} data-testid={`subscribe-btn-${fund.fund_id}`}>
                  Subscribe
                </GoldButton>
              </div>
            </Card>
          ))}
        </div>
      )}

      {myFunds.length === 0 && funds.filter(f => ["raising", "open"].includes(f.status)).length === 0 && (
        fundPortalOpps.length > 0 ? (
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-[#333] uppercase tracking-wider">Available Opportunities</h3>
            {fundPortalOpps.map((opp) => (
              <Card key={opp.opportunity_id} hover={false}>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-bold text-[#0A0A0A] flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-gold-dark" /> {opp.title || "Investment Opportunity"}
                    </h4>
                    <p className="text-xs text-[#888] mt-1">
                      Target: {fmtUSD(opp.target_raise || 0)} |
                      {opp.projected_yield ? ` ${opp.projected_yield}% yield |` : ""}
                      {opp.projected_term_months ? ` ${opp.projected_term_months}mo term` : ""}
                    </p>
                  </div>
                  <span className="text-xs bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full font-bold border border-emerald-200">
                    {opp.status?.toUpperCase() || "OPEN"}
                  </span>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Building2 className="h-12 w-12 text-[#ccc] mx-auto mb-3" />
            <p className="text-sm text-[#888]">No fund opportunities available yet. Check back soon.</p>
          </div>
        )
      )}

      {/* Subscribe Modal */}
      {subscribeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="subscribe-modal">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 w-full max-w-md border border-cream-warm shadow-xl">
            <h3 className="text-lg font-bold text-[#0A0A0A] mb-1">Subscribe to {subscribeModal.name}</h3>
            <p className="text-xs text-[#888] mb-4">
              Minimum: ${(subscribeModal.min_subscription || 0).toLocaleString()} |
              {subscribeModal.preferred_return_pct}% preferred return |
              {subscribeModal.carried_interest_pct}% carried interest
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-[#333] mb-1">Subscription Amount ($)</label>
              <input type="number" value={subAmount} onChange={e => setSubAmount(e.target.value)}
                placeholder={`Min: ${fmtUSD(subscribeModal.min_subscription || 50000)}`}
                className="w-full rounded-lg border border-cream-warm px-4 py-2.5 text-sm focus:border-emerald-500 outline-none"
                data-testid="subscription-amount-input" />
            </div>
            <div className="p-3 rounded-lg bg-cream border border-cream-warm mb-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-[#666]">
                  Your subscription will require admin approval. Capital will be drawn through capital calls as deals are funded.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setSubscribeModal(null)} className="flex-1 px-4 py-2 rounded-lg border border-cream-warm text-sm text-[#666] hover:bg-cream transition-colors">Cancel</button>
              <GoldButton onClick={handleSubscribe} disabled={submitting || !subAmount} className="flex-1" data-testid="confirm-subscribe">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> : null}
                Subscribe
              </GoldButton>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
